import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { social } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import StarRating from "../components/social/StarRating";
import UserChip from "../components/common/UserChip";
import LoadingOverlay from "../components/LoadingOverlay";

export default function RatePage() {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stars, setStars] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [ratedId, setRatedId] = useState("");
  const [otherParty, setOtherParty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function resolveOtherParty() {
      try {
        const { data: response } = await supabase
          .from("responses")
          .select("post_id, responder_id, completed_at")
          .eq("id", responseId)
          .single();

        if (!response) {
          setError("Response not found");
          setLoading(false);
          return;
        }

        if (!response.completed_at) {
          setError("This response has not been completed yet");
          setLoading(false);
          return;
        }

        const { data: post } = await supabase
          .from("posts")
          .select("author_id")
          .eq("id", response.post_id)
          .single();

        if (!post) {
          setError("Post not found");
          setLoading(false);
          return;
        }

        const otherPartyId =
          user.id === post.author_id
            ? response.responder_id
            : post.author_id;

        const { data: otherUser } = await supabase
          .from("users")
          .select("id, display_name, avatar_url, is_verified, rank, rating_avg, rating_count")
          .eq("id", otherPartyId)
          .single();

        if (otherUser) {
          setOtherParty(otherUser);
          setRatedId(otherPartyId);
        } else {
          setError("Could not find the other party");
        }
      } catch {
        setError("Failed to load rating information");
      } finally {
        setLoading(false);
      }
    }

    if (user?.id) {
      resolveOtherParty();
    }
  }, [responseId, user?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (stars === 0) {
      setError("Please select a rating");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await social.submitRating(responseId, {
        rater_id: user.id,
        rated_id: ratedId,
        stars,
        feedback,
      });
      setResult(res);
    } catch (err) {
      setError(err.error || err.detail || "Failed to submit rating");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingOverlay />;

  if (!loading && error && !otherParty) {
    return (
      <div className="max-w-md mx-auto text-center py-12">
        <p className="text-coral mb-4">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-terracotta text-white rounded-md font-medium hover:bg-terracotta-dark transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (result) {
    return (
      <div className="max-w-md mx-auto text-center py-12">
        {result.cp_awarded != null && (
          <p className="text-lg text-amber font-semibold mb-4">
            +{result.cp_awarded} Community Points awarded
          </p>
        )}
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-terracotta text-white rounded-md font-medium hover:bg-terracotta-dark transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-walnut-light hover:text-walnut mb-4">
        <ArrowLeft size={16} /> Back
      </button>
      <form onSubmit={handleSubmit} className="space-y-5">
        {otherParty && (
          <div>
            <label className="block text-sm font-medium text-walnut mb-2">Reviewing</label>
            <div className="p-3 bg-cream rounded-lg">
              <UserChip user={otherParty} />
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-walnut mb-2">Rating</label>
          <StarRating value={stars} onChange={setStars} size={32} />
        </div>
        <div>
          <label className="block text-sm font-medium text-walnut mb-2">Written Feedback</label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            required
            rows={4}
            placeholder="How was your experience?"
            className="w-full px-3 py-2.5 bg-cream border border-stone rounded-md text-sm text-walnut placeholder:text-walnut-light/60 focus:outline-none focus:ring-2 focus:ring-terracotta/30 resize-y"
          />
        </div>
        {error && <p className="text-sm text-coral">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 bg-terracotta text-white rounded-md font-medium hover:bg-terracotta-dark transition-colors disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Submit Review"}
        </button>
      </form>
    </div>
  );
}
